package dev.terrakok.mapitan

import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import dev.terrakok.mapitan.domain.Document
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.spatialk.geojson.Position
import kotlin.math.PI
import kotlin.math.atan
import kotlin.math.ln
import kotlin.math.log2
import kotlin.math.min
import kotlin.math.sinh
import kotlin.math.tan

private const val MAX_CAMERA_ZOOM = 25.5
private const val INITIAL_DOCUMENT_MAX_ZOOM_PERCENT = 60.0
private const val MERCATOR_TILE_SIZE = 512.0
private const val MAX_MERCATOR_LATITUDE = 85.05112878
private val INITIAL_DOCUMENT_PADDING = 96.dp

internal fun Document.cameraPositionFor(viewportSize: DpSize): CameraPosition? {
    val positions = buildList {
        addAll(pins.map { it.point.coordinates })
        tracks.forEach { track -> addAll(track.points.map { it.point.coordinates }) }
        areas.forEach { area -> addAll(area.points.map { it.point.coordinates }) }
        addAll(labels.map { it.point.coordinates })
    }.filter { it.longitude.isFinite() && it.latitude.isFinite() }
    if (positions.isEmpty()) return null

    val longitudes = positions.map { normalizeLongitude(it.longitude) }.sorted()
    val (longitudeStart, longitudeSpan) = smallestLongitudeArc(longitudes)
    val longitudeCenter = normalizeLongitude(longitudeStart + longitudeSpan / 2.0) - 180.0

    val mercatorYs = positions.map { mercatorY(it.latitude) }
    val minY = mercatorYs.min()
    val maxY = mercatorYs.max()
    val latitudeCenter = mercatorLatitude((minY + maxY) / 2.0)

    val availableWidth = (viewportSize.width - INITIAL_DOCUMENT_PADDING * 2).value.coerceAtLeast(1f).toDouble()
    val availableHeight = (viewportSize.height - INITIAL_DOCUMENT_PADDING * 2).value.coerceAtLeast(1f).toDouble()
    val horizontalSpan = longitudeSpan / 360.0
    val verticalSpan = maxY - minY
    val horizontalZoom = zoomForSpan(availableWidth, horizontalSpan)
    val verticalZoom = zoomForSpan(availableHeight, verticalSpan)
    val maxZoom = MAX_CAMERA_ZOOM * INITIAL_DOCUMENT_MAX_ZOOM_PERCENT / 100.0
    val zoom = min(horizontalZoom, verticalZoom).coerceIn(0.0, maxZoom)

    return CameraPosition(
        target = Position(longitudeCenter, latitudeCenter),
        zoom = zoom,
    )
}

/** Returns the start and width of the smallest circular arc containing all longitudes. */
private fun smallestLongitudeArc(sortedLongitudes: List<Double>): Pair<Double, Double> {
    if (sortedLongitudes.size == 1) return sortedLongitudes.single() to 0.0

    var largestGap = -1.0
    var arcStart = sortedLongitudes.first()
    sortedLongitudes.indices.forEach { index ->
        val current = sortedLongitudes[index]
        val next = if (index == sortedLongitudes.lastIndex) {
            sortedLongitudes.first() + 360.0
        } else {
            sortedLongitudes[index + 1]
        }
        val gap = next - current
        if (gap > largestGap) {
            largestGap = gap
            arcStart = normalizeLongitude(next)
        }
    }
    return arcStart to 360.0 - largestGap
}

private fun normalizeLongitude(longitude: Double): Double =
    ((longitude + 180.0) % 360.0 + 360.0) % 360.0

private fun mercatorY(latitude: Double): Double {
    val radians = latitude.coerceIn(-MAX_MERCATOR_LATITUDE, MAX_MERCATOR_LATITUDE) * PI / 180.0
    return (1.0 - ln(tan(PI / 4.0 + radians / 2.0)) / PI) / 2.0
}

private fun mercatorLatitude(y: Double): Double = atan(sinh(PI * (1.0 - 2.0 * y))) * 180.0 / PI

private fun zoomForSpan(availableSize: Double, normalizedSpan: Double): Double =
    if (normalizedSpan == 0.0) Double.POSITIVE_INFINITY
    else log2(availableSize / (MERCATOR_TILE_SIZE * normalizedSpan))
